import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../translations/translations';

const YouTubeFeed = ({ excludeShorts = false, showAll = false }) => {
  const { language } = useLanguage();
  const { darkMode } = useTheme();
  const [allVideos, setAllVideos] = useState([]);
  const [visibleCount, setVisibleCount] = useState(showAll ? 6 : 6);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loaderRef = useRef(null);

  const CHANNEL_URL = 'https://www.youtube.com/@EffektNorge';

  useEffect(() => {
    const fetchVideos = async () => {
      // Primary source is our own serverless function, which scrapes the
      // public channel page and returns JSON. YouTube's RSS endpoint
      // (`/feeds/videos.xml`) now 404s from datacenter IPs, so we no longer
      // ask the client-side proxies to fetch RSS — they'd hit the same wall.
      try {
        const response = await fetch('/api/youtube-feed', {
          headers: { Accept: 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          let videoData = Array.isArray(data?.videos) ? data.videos : [];
          if (excludeShorts) {
            videoData = videoData.filter(v => !v.isShort);
          }
          if (videoData.length > 0) {
            setAllVideos(videoData);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('YouTube feed endpoint failed:', err.message);
      }

      console.error('Could not load YouTube feed');
      setError('Failed to load videos');
      setLoading(false);
    };

    fetchVideos();
  }, [excludeShorts]);

  // Infinite scroll - load more when sentinel enters viewport
  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && visibleCount < allVideos.length) {
      setVisibleCount(prev => Math.min(prev + 6, allVideos.length));
    }
  }, [visibleCount, allVideos.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { rootMargin: '200px' });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const videos = allVideos.slice(0, visibleCount);

  if (loading) {
    return (
      <div className="w-full py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-400">{t(language, 'youtube.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || allVideos.length === 0) {
    return (
      <div className="w-full py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-black text-teal-400 mb-6">
              {t(language, 'youtube.title')}
            </h2>
            <a
              href={CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all duration-200"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              {t(language, 'youtube.visitChannel')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
          <h2 className={`text-2xl sm:text-3xl font-black ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
            {t(language, 'youtube.title')}
          </h2>
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-lg transition-all duration-200 text-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            {t(language, 'youtube.subscribe')}
          </a>
        </div>

        {/* Videos Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <a
              key={video.id}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`group block rounded-xl overflow-hidden border transition-all duration-150 hover:scale-[1.02] ${darkMode ? 'bg-slate-800 bg-opacity-60 border-teal-500 border-opacity-20 hover:border-opacity-50 hover:shadow-teal-500/10' : 'bg-white border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md'}`}
            >
              {/* Thumbnail */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-300">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                    <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Video Info */}
              <div className="p-4">
                <h3 className={`font-bold text-sm sm:text-base line-clamp-2 transition-colors duration-150 ${darkMode ? 'text-gray-100 group-hover:text-teal-400' : 'text-stone-800 group-hover:text-teal-600'}`}>
                  {video.title}
                </h3>
              </div>
            </a>
          ))}
        </div>

        {/* Infinite scroll sentinel */}
        {visibleCount < allVideos.length && (
          <div ref={loaderRef} className="flex justify-center py-8">
            <div className={`w-6 h-6 border-2 rounded-full animate-spin ${darkMode ? 'border-teal-500 border-t-transparent' : 'border-teal-600 border-t-transparent'}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubeFeed;
